import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, CheckCircle2, Archive } from "lucide-react";
import Modal from "@/components/admin/Modal";
import { Spinner, EmptyState, Badge } from "@/components/common/Primitives";
import { formatDateShort } from "@/lib/format";
import {
  formatSubmissionValue,
  getOrderedSubmissionRows,
  getSubmissionSubmitterName,
} from "@/lib/submissionDisplay";
import { formsService, formsCrud } from "@/services/formsService";

const STATUS_TONES = { new: "gold", reviewed: "green", archived: "gray" };

export default function AdminSubmissionsPage() {
  const [submissions, setSubmissions] = useState(null);
  const [forms, setForms] = useState([]);
  const [filter, setFilter] = useState("All");
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    const [subs, allForms] = await Promise.all([
      formsService.listSubmissions(filter),
      formsCrud.list(),
    ]);
    setSubmissions(subs);
    setForms(allForms);
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  async function setStatus(sub, status) {
    try {
      await formsService.setSubmissionStatus(sub.id, status);
      toast.success(`Marked as ${status}`);
      await load();
    } catch (err) {
      toast.error(err.message || "Update failed");
    }
  }

  return (
    <div data-testid="admin-submissions-page">
      <h1 className="font-serif text-3xl font-semibold text-ink">Submissions</h1>
      <p className="mt-1 text-sm text-ink-muted">Entries received through the public online forms.</p>

      <div className="mt-5 flex flex-wrap gap-2">
        {["All", ...forms.map((f) => f.slug)].map((slug) => (
          <button
            key={slug}
            onClick={() => setFilter(slug)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              filter === slug ? "bg-maroon text-white" : "bg-white border border-gray-200 text-ink-soft hover:text-maroon"
            }`}
            data-testid={`submissions-filter-${slug}`}
          >
            {slug === "All" ? "All forms" : forms.find((f) => f.slug === slug)?.title || slug}
          </button>
        ))}
      </div>

      <div className="mt-6 bg-white rounded-xl border border-gray-200 shadow-card overflow-hidden">
        {!submissions ? (
          <Spinner />
        ) : submissions.length === 0 ? (
          <EmptyState title="No submissions" message="Submissions from public forms will appear here." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-ink-muted bg-cream-alt/60 border-b border-gray-100">
                  <th className="px-5 py-3 font-semibold">Submitted by</th>
                  <th className="px-5 py-3 font-semibold">Form</th>
                  <th className="px-5 py-3 font-semibold">Date</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map((s) => (
                  <tr key={s.id} className="border-b border-gray-50 hover:bg-cream/70 transition-colors" data-testid={`submission-row-${s.id}`}>
                    <td className="px-5 py-3.5 font-medium text-ink">
                      {getSubmissionSubmitterName(s.data)}
                    </td>
                    <td className="px-5 py-3.5 text-ink-soft">{s.form_slug}</td>
                    <td className="px-5 py-3.5 text-ink-muted">{formatDateShort(s.created_at)}</td>
                    <td className="px-5 py-3.5">
                      <Badge tone={STATUS_TONES[s.status] || "gray"}>{s.status}</Badge>
                    </td>
                    <td className="px-5 py-3.5 text-right whitespace-nowrap">
                      <button onClick={() => setViewing(s)} className="p-1.5 rounded-md text-ink-muted hover:text-maroon hover:bg-maroon-50 transition-colors" title="View" data-testid={`view-submission-${s.id}`}>
                        <Eye size={15} />
                      </button>
                      <button onClick={() => setStatus(s, "reviewed")} className="p-1.5 rounded-md text-ink-muted hover:text-green-700 hover:bg-green-50 transition-colors" title="Mark reviewed" data-testid={`review-submission-${s.id}`}>
                        <CheckCircle2 size={15} />
                      </button>
                      <button onClick={() => setStatus(s, "archived")} className="p-1.5 rounded-md text-ink-muted hover:text-ink hover:bg-gray-100 transition-colors" title="Archive" data-testid={`archive-submission-${s.id}`}>
                        <Archive size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={!!viewing} onClose={() => setViewing(null)} title="Submission details">
        {viewing && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Badge tone={STATUS_TONES[viewing.status] || "gray"}>{viewing.status}</Badge>
              <span className="text-xs text-ink-muted">
                {viewing.form_slug} · {formatDateShort(viewing.created_at)}
              </span>
            </div>
            <p className="text-sm font-medium text-ink">
              {getSubmissionSubmitterName(viewing.data)}
            </p>
            <dl className="divide-y divide-gray-50 border border-gray-100 rounded-lg overflow-hidden">
              {getOrderedSubmissionRows(viewing.form_slug, viewing.data, forms).map((row) => (
                <div key={row.key} className="px-4 py-3 grid grid-cols-3 gap-3 text-sm">
                  <dt className="text-ink-muted">{row.label}</dt>
                  <dd className="col-span-2 text-ink break-words">
                    {formatSubmissionValue(row.value)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </Modal>
    </div>
  );
}
